import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { AIAssistantNodeType, AIAssistantNode, AIAssistantSearchNode } from 'src/types/ai-assistant'
import { getAIAssistantSop, chatWithAIAssistant } from 'src/services/ai-assistant'
import { message } from 'antd'
import { v4 as uuid } from 'uuid'

// ========== 类型定义 ==========
interface CallWorkbenchState {
  // 状态
  nodeList: AIAssistantNode[]
  sopNodeLoading: boolean
  searchNodeLoading: boolean

  // 异步操作方法
  getSopNode: (params: { nodeId: string; leadId: number; cid: string }) => Promise<void>
  getAISearchNode: (params: { type: string; content: string; label?: string }) => Promise<void>

  // 同步操作方法
  setNodeList: (nodeList: AIAssistantNode[] | ((draft: AIAssistantNode[]) => void)) => void
  setSearchNodeLoading: (loading: boolean) => void

  // 重置方法
  reset: () => void
}

// ========== 初始状态 ==========
const initialState = {
  nodeList: [] as AIAssistantNode[],
  sopNodeLoading: false,
  searchNodeLoading: false,
}

// ========== 创建 Store ==========
export const callWorkbenchStore = create<CallWorkbenchState>()(
  immer((set) => ({
    ...initialState,

    // ========== 异步操作方法 ==========
    /**
     * 获取 SOP 节点
     * 对应原 Hook 的 getSopNode 方法
     */
    getSopNode: async ({ nodeId, leadId, cid }) => {
      try {
        set((state) => {
          state.sopNodeLoading = true
        })

        const [res, err] = await getAIAssistantSop({ nodeId, leadId, cid })

        if (err) {
          message.error(err.message)
          set((state) => {
            state.sopNodeLoading = false
          })
          return
        }

        set((state) => {
          state.nodeList.push({
            type: AIAssistantNodeType.assistant,
            node: res?.node,
            id: res?.node?.id,
          })
          state.sopNodeLoading = false
        })
      } catch (error) {
        console.error('获取 SOP 节点失败:', error)
        message.error(error?.message)
        set((state) => {
          state.sopNodeLoading = false
        })
      }
    },

    /**
     * 获取 AI 搜索节点
     * 对应原 Hook 的 getAISearchNode 方法
     * 支持流式响应处理
     */
    getAISearchNode: async ({ type, content, label }) => {
      try {
        const id = uuid()

        set((state) => {
          state.searchNodeLoading = true
        })

        // 调用 AI 服务，处理流式响应
        await chatWithAIAssistant({ type, content, reqId: id }, {
          onStart: () => {
            // ✅ 关键：只有 API 真正响应（onStart 触发），才添加节点
            const node: AIAssistantSearchNode = {
              id,
              label: label || content,
              words: '',
              done: false,
            }
            set((state) => {
              state.nodeList.push({ id, type: AIAssistantNodeType.aiSearch, node })
            })
          },
          onData: (data) => {
            const word = data ? JSON.parse(data)?.content : ''
            set((state) => {
              const nodeIndex = state.nodeList.findIndex(n => n.id === id)
              if (nodeIndex !== -1 && state.nodeList[nodeIndex].node) {
                const searchNode = state.nodeList[nodeIndex].node as AIAssistantSearchNode
                searchNode.words += word
              }
            })
          },
          onError: (error) => {
            console.error('AI 搜索错误:', error)
            message.error(error?.message || 'AI 搜索失败')
            set((state) => {
              state.searchNodeLoading = false
            })
          },
          onEnd: () => {
            set((state) => {
              const nodeIndex = state.nodeList.findIndex(n => n.id === id)
              if (nodeIndex !== -1 && state.nodeList[nodeIndex].node) {
                const searchNode = state.nodeList[nodeIndex].node as AIAssistantSearchNode
                searchNode.done = true
              }
              state.searchNodeLoading = false
            })
          },
        })
      } catch (error) {
        console.error('获取 AI 搜索节点失败:', error)
        message.error('获取 AI 搜索节点失败')
        set((state) => {
          state.searchNodeLoading = false
        })
      }
    },

    // ========== 同步操作方法 ==========
    /**
     * 设置节点列表
     */
    setNodeList: (nodeList) => {
      set((state) => {
        if (typeof nodeList === 'function') {
          nodeList(state.nodeList)
        } else {
          state.nodeList = nodeList
        }
      })
    },

    /**
     * 设置搜索加载状态
     */
    setSearchNodeLoading: (loading) => {
      set((state) => {
        state.searchNodeLoading = loading
      })
    },

    // ========== 重置方法 ==========
    /**
     * 重置所有状态到初始值
     */
    reset: () => {
      set(initialState)
    },
  }))
)
